import React, { useEffect, useState } from 'react'
import { useUserStore } from '@store'
import { Users as UsersIcon, UserPlus, Eye, Edit2, Trash2, RotateCcw, UserX, UserCheck, Key, TrendingUp, AlertCircle, XCircle, Filter, ArrowUpDown, User } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader, ResetPasswordModal } from '@components/common'
import UserForm from './UserForm'
import UserView from './UserView'
import './Users.css'

/**
 * Get user initials from name
 */
const getInitials = (firstName, lastName) => {
	const first = firstName?.charAt(0)?.toUpperCase() || ''
	const last = lastName?.charAt(0)?.toUpperCase() || ''
	return first + last || '?'
}

/**
 * Format last login timestamp
 */
const formatLastLogin = (timestamp) => {
	if (!timestamp) return 'Never'

	const now = new Date()
	const loginDate = new Date(timestamp)
	const diffInSeconds = Math.floor((now - loginDate) / 1000)

	if (diffInSeconds < 60) return 'Just now'
	if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
	if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
	if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`

	return loginDate.toLocaleDateString()
}

/**
 * Users Management Page (Super Admin Only)
 *
 * Complete user management interface with:
 * - List view with DataTable
 * - Search and filtering by company, role, status
 * - Create/Edit/View/Delete/Block/Unblock operations
 * - Statistics dashboard
 * - Pagination
 * - Responsive design
 *
 * Features:
 * - Full CRUD operations
 * - Real-time search
 * - Advanced filtering (company, role, status)
 * - User blocking/unblocking
 * - Password reset
 * - Soft delete with restore
 * - Confirmation dialogs
 * - Loading states
 * - Empty states
 * - Error handling
 */
const Users = () => {
	const {
		users,
		selectedUser,
		loading,
		filters,
		pagination,
		stats,
		companies,
		roles,
		fetchUsers,
		fetchCompanies,
		fetchRoles,
		selectUser,
		deleteUser,
		restoreUser,
		blockUser,
		unblockUser,
		resetUserPassword,
		setSearch,
		setCompanyFilter,
		setRoleFilter,
		setStatusFilter,
		setPage,
		nextPage,
		prevPage,
	} = useUserStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
	const [actionType, setActionType] = useState(null) // 'delete', 'restore', 'block', 'unblock'
	const [selectedUserForAction, setSelectedUserForAction] = useState(null)
	const [resetPasswordLoading, setResetPasswordLoading] = useState(false)

	// Fetch users, companies, and roles on mount
	useEffect(() => {
		fetchUsers()
		fetchCompanies()
		fetchRoles()
	}, [])

	/**
	 * Handle create user
	 */
	const handleCreate = () => {
		selectUser(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view user
	 */
	const handleView = (user) => {
		selectUser(user)
		setShowViewModal(true)
	}

	/**
	 * Handle edit user
	 */
	const handleEdit = (user) => {
		selectUser(user)
		setShowEditModal(true)
	}

	/**
	 * Handle delete user
	 */
	const handleDelete = (user) => {
		setSelectedUserForAction(user)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore user
	 */
	const handleRestore = (user) => {
		setSelectedUserForAction(user)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle block user
	 */
	const handleBlock = (user) => {
		setSelectedUserForAction(user)
		setActionType('block')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle unblock user
	 */
	const handleUnblock = (user) => {
		setSelectedUserForAction(user)
		setActionType('unblock')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle reset password - opens modal for manual password entry
	 */
	const handleResetPassword = (user) => {
		setSelectedUserForAction(user)
		setShowResetPasswordModal(true)
	}

	/**
	 * Submit password reset with manually entered password
	 */
	const handleResetPasswordSubmit = async (newPassword) => {
		if (!selectedUserForAction) return

		setResetPasswordLoading(true)
		try {
			await resetUserPassword(selectedUserForAction.id, newPassword)
			setShowResetPasswordModal(false)
			setSelectedUserForAction(null)
		} catch (error) {
			console.error('Password reset failed:', error)
		} finally {
			setResetPasswordLoading(false)
		}
	}

	/**
	 * Confirm action (delete/restore/block/unblock)
	 */
	const confirmAction = async () => {
		if (!selectedUserForAction) return

		try {
			if (actionType === 'delete') {
				await deleteUser(selectedUserForAction.id)
			} else if (actionType === 'restore') {
				await restoreUser(selectedUserForAction.id)
			} else if (actionType === 'block') {
				await blockUser(selectedUserForAction.id)
			} else if (actionType === 'unblock') {
				await unblockUser(selectedUserForAction.id)
			}

			setShowConfirmDialog(false)
			setSelectedUserForAction(null)
			setActionType(null)
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedUserForAction(null)
		setActionType(null)
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status) => {
		if (status === 'active') {
			return <span className="badge badge--active">Active</span>
		} else if (status === 'suspended') {
			return <span className="badge badge--suspended">Suspended</span>
		}
		return <span className="badge badge--inactive">Inactive</span>
	}

	/**
	 * Get role badge
	 */
	const getRoleBadge = (roleSlug, roleName) => {
		let badgeClass = 'badge'

		if (roleSlug === 'super-admin') {
			badgeClass += ' badge--danger'
		} else if (roleSlug === 'company-admin') {
			badgeClass += ' badge--primary'
		} else if (roleSlug === 'compliance-manager') {
			badgeClass += ' badge--info'
		} else if (roleSlug === 'auditor') {
			badgeClass += ' badge--warning'
		} else {
			badgeClass += ' badge--secondary'
		}

		return <span className={badgeClass}>{roleName}</span>
	}

	/**
	 * Get action confirmation details
	 */
	const getConfirmDetails = () => {
		if (!selectedUserForAction || !actionType) return {}

		const userName = `${selectedUserForAction.first_name} ${selectedUserForAction.last_name}`

		const details = {
			delete: {
				title: 'Delete User?',
				message: `Are you sure you want to delete "${userName}"? This will soft delete the user account, but data will be preserved.`,
				confirmText: 'Delete',
				variant: 'danger',
			},
			restore: {
				title: 'Restore User?',
				message: `Are you sure you want to restore "${userName}"? This will reactivate the user account.`,
				confirmText: 'Restore',
				variant: 'info',
			},
			block: {
				title: 'Block User?',
				message: `Are you sure you want to block "${userName}"? This will suspend the user account and prevent login.`,
				confirmText: 'Block',
				variant: 'warning',
			},
			unblock: {
				title: 'Unblock User?',
				message: `Are you sure you want to unblock "${userName}"? This will restore access to the user account.`,
				confirmText: 'Unblock',
				variant: 'success',
			},
		}

		return details[actionType] || {}
	}

	// Define table columns
	const columns = [
		{
			key: 'avatar',
			label: 'Avatar',
			align: 'center',
			render: (user) => (
				<div className="user-avatar-cell">
					{user.avatar_url ? (
						<img src={user.avatar_url} alt={`${user.first_name} ${user.last_name}`} className="user-avatar-cell__image" />
					) : (
						<div className="user-avatar-cell__initials">
							{getInitials(user.first_name, user.last_name)}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'name',
			label: 'Name',
			sortable: true,
			render: (user) => (
				<div className="user-cell">
					<div className="user-cell__name">{`${user.first_name} ${user.last_name}`}</div>
					<div className="user-cell__email">{user.email}</div>
				</div>
			),
		},
		{
			key: 'email',
			label: 'Email',
			sortable: true,
			render: (user) => (
				<div className="email-cell">
					{user.email}
				</div>
			),
		},
		{
			key: 'role',
			label: 'Role',
			sortable: true,
			align: 'center',
			render: (user) => getRoleBadge(user.role_slug, user.role_name),
		},
		{
			key: 'company',
			label: 'Company',
			sortable: true,
			render: (user) => (
				<div className="company-cell">
					{user.company_name || '-'}
				</div>
			),
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (user) => getStatusBadge(user.status),
		},
		{
			key: 'last_login',
			label: 'Last Login',
			sortable: true,
			render: (user) => (
				<div className="last-login-cell">
					{formatLastLogin(user.last_login_at)}
				</div>
			),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (user) => user.status !== 'suspended',
		},
		{
			label: 'Block User',
			icon: <UserX size={16} />,
			onClick: handleBlock,
			variant: 'warning',
			show: (user) => user.status !== 'suspended',
		},
		{
			label: 'Unblock User',
			icon: <UserCheck size={16} />,
			onClick: handleUnblock,
			variant: 'success',
			show: (user) => user.status === 'suspended',
		},
		{
			label: 'Reset Password',
			icon: <Key size={16} />,
			onClick: handleResetPassword,
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (user) => !user.deleted_at,
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (user) => user.deleted_at,
		},
	]

	const confirmDetails = getConfirmDetails()

	return (
		<div className="users-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="Users"
				description="Manage user accounts and permissions"
				breadcrumbs={[{ label: 'Management' }, { label: 'Users' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<UserPlus size={20} />
						<span>Add User</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<UsersIcon size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Users</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<UserCheck size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active Users</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<AlertCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Inactive Users</div>
						<div className="stat-card__value">{stats.inactive}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--danger">
						<XCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Suspended Users</div>
						<div className="stat-card__value">{stats.suspended}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search users by name or email..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.company}
						onChange={(e) => setCompanyFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Companies' },
							...companies.map(company => ({
								value: company.id,
								label: company.name
							}))
						]}
					/>

					<Select
						value={filters.role}
						onChange={(e) => setRoleFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Roles' },
							...roles.map(role => ({
								value: role.id,
								label: role.name
							}))
						]}
					/>

					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'inactive', label: 'Inactive' },
							{ value: 'suspended', label: 'Suspended' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={users}
				actions={actions}
				loading={loading}
				emptyMessage="No users found. Create your first user to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && (
				<UserForm
					mode="create"
					onClose={() => setShowCreateModal(false)}
				/>
			)}

			{/* Edit Modal */}
			{showEditModal && (
				<UserForm
					mode="edit"
					onClose={() => setShowEditModal(false)}
				/>
			)}

			{/* View Modal */}
			{showViewModal && selectedUser && (
				<UserView
					userData={selectedUser}
					onClose={() => setShowViewModal(false)}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
				/>
			)}

			{/* Confirm Dialog (Delete/Restore/Block/Unblock) */}
			{showConfirmDialog && selectedUserForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={confirmDetails.title}
					message={confirmDetails.message}
					confirmText={confirmDetails.confirmText}
					variant={confirmDetails.variant}
				/>
			)}

			{/* Reset Password Modal */}
			<ResetPasswordModal
				isOpen={showResetPasswordModal}
				onClose={() => {
					setShowResetPasswordModal(false)
					setSelectedUserForAction(null)
				}}
				onSubmit={handleResetPasswordSubmit}
				user={selectedUserForAction}
				loading={resetPasswordLoading}
			/>
		</div>
	)
}

export default Users
